import React, { useState, useEffect } from 'react';
import Button from '../common/Button';
import ServiceList from './ServiceList';
import AddServiceModal from './AddServiceModal';
import AddClinicServiceModal from './AddClinicServiceModal';
import EditClinicServiceModal from './EditClinicServiceModal';
import ConfirmDialog from '../common/ConfirmDialog';
import { getAllServices, getClinicServices, deleteClinicService } from '../../services/api';

const ClinicServicesTab = ({ clinicId }) => {
    const [services, setServices] = useState([]);
    const [clinicServices, setClinicServices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showAddServiceModal, setShowAddServiceModal] = useState(false);
    const [showAddClinicServiceModal, setShowAddClinicServiceModal] = useState(false);
    const [showEditClinicServiceModal, setShowEditClinicServiceModal] = useState(false);
    const [selectedService, setSelectedService] = useState(null);
    const [selectedClinicService, setSelectedClinicService] = useState(null);
    const [activeTab, setActiveTab] = useState('available'); // 'available' or 'clinic'
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [serviceToDelete, setServiceToDelete] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    useEffect(() => {
        if (activeTab === 'available') {
            fetchServices();
            fetchClinicServices();
        }
    }, [activeTab, clinicId]);

    const fetchServices = async () => {
        setLoading(true);
        try {
            const response = await getAllServices();
            if (response.success) {
                setServices(response.data || []);
            } else {
                console.error('Failed to fetch services:', response.message);
            }
        } catch (error) {
            console.error('Error fetching services:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchClinicServices = async () => {
        try {
            const response = await getClinicServices(clinicId);
            if (response.success) {
                setClinicServices(response.data || []);
            } else {
                console.error('Failed to fetch clinic services:', response.message);
            }
        } catch (error) {
            console.error('Error fetching clinic services:', error);
        }
    };

    const handleAddService = () => {
        setShowAddServiceModal(true);
    };

    const handleServiceCreated = (newService) => {
        setServices(prev => [newService, ...prev]);
        setShowAddServiceModal(false);
    };

    const handleSelectService = (service) => {
        setSelectedService(service);
        setShowAddClinicServiceModal(true);
    };

    const handleClinicServiceAdded = (newClinicService) => {
        setClinicServices(prev => [newClinicService, ...prev]);
        setShowAddClinicServiceModal(false);
        setSelectedService(null);
    };

    const handleEditService = (clinicService) => {
        setSelectedClinicService(clinicService);
        setShowEditClinicServiceModal(true);
    };

    const handleClinicServiceUpdated = (updatedClinicService) => {
        setClinicServices(prev => prev.map(service => 
            service.id === updatedClinicService.id ? updatedClinicService : service
        ));
        setShowEditClinicServiceModal(false);
        setSelectedClinicService(null);
    };

    const handleDeleteService = (clinicService) => {
        setServiceToDelete(clinicService);
        setShowDeleteConfirm(true);
    };

    const confirmDelete = async () => {
        if (!serviceToDelete) return;

        setDeleteLoading(true);
        try {
            const response = await deleteClinicService(serviceToDelete.id);
            
            if (response.success) {
                setClinicServices(prev => prev.filter(service => service.id !== serviceToDelete.id));
                setShowDeleteConfirm(false);
                setServiceToDelete(null);
            } else {
                console.error('Failed to delete clinic service:', response.message);
                // You could add a toast notification here
            }
        } catch (error) {
            console.error('Error deleting clinic service:', error);
        } finally {
            setDeleteLoading(false);
        }
    };

    const cancelDelete = () => {
        setShowDeleteConfirm(false);
        setServiceToDelete(null);
        setDeleteLoading(false);
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(price);
    };

    const formatDuration = (minutes) => {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        if (hours > 0) {
            return `${hours}h ${mins > 0 ? `${mins}m` : ''}`.trim();
        }
        return `${mins}m`;
    };

    const renderClinicServices = () => {
        if (!clinicServices || clinicServices.length === 0) {
            return (
                <div className="text-center py-12">
                    <div className="text-gray-400 mb-4">
                        <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                    </div>
                    <p className="text-gray-500">No services added to this clinic yet.</p>
                    <p className="text-gray-400 text-sm">Switch to "Available Services" to add services.</p>
                </div>
            );
        }

        return (
            <div className="space-y-4">
                {clinicServices.map((clinicService) => (
                    <div key={clinicService.id} className="bg-white rounded-lg shadow-sm border p-4">
                        <div className="flex justify-between items-start">
                            <div className="flex-1">
                                <h4 className="text-lg font-medium text-gray-900 mb-1">
                                    {clinicService.service?.name || 'Unknown Service'}
                                </h4>
                                <p className="text-sm text-gray-600 mb-2">
                                    {clinicService.description || clinicService.service?.description}
                                </p>
                                <div className="flex items-center space-x-4 text-sm">
                                    <span className="text-green-600 font-medium">
                                        {formatPrice(clinicService.basePrice)}
                                    </span>
                                    <span className="text-gray-500">
                                        {formatDuration(clinicService.durationMinutes)}
                                    </span>
                                    
                                </div>
                            </div>
                            <div className="flex items-center space-x-2">
                                <Button
                                    type="button"
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => handleEditService(clinicService)}
                                >
                                    Edit
                                </Button>
                                <Button
                                    type="button"
                                    variant="danger"
                                    size="sm"
                                    onClick={() => handleDeleteService(clinicService)}
                                >
                                    Remove
                                </Button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="space-y-6">
            {/* Tab Navigation */}
            <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8">
                    <button
                        onClick={() => setActiveTab('available')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'available'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        Available Services
                    </button>
                    <button
                        onClick={() => setActiveTab('clinic')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'clinic'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        Clinic Services
                        {clinicServices.length > 0 && (
                            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {clinicServices.length}
                            </span>
                        )}
                    </button>
                </nav>
            </div>

            {/* Tab Content */}
            <div>
                {activeTab === 'available' ? (
                    <ServiceList
                        services={services}
                        clinicServices={clinicServices}
                        onAddService={handleAddService}
                        onSelectService={handleSelectService}
                        loading={loading}
                    />
                ) : (
                    <div>
                        <div className="flex justify-between items-center mb-6">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">Clinic Services</h3>
                                <p className="text-sm text-gray-500">
                                    Services currently offered by this clinic
                                </p>
                            </div>
                        </div>
                        {renderClinicServices()}
                    </div>
                )}
            </div>

            {/* Modals */}
            <AddServiceModal
                isOpen={showAddServiceModal}
                onClose={() => setShowAddServiceModal(false)}
                onSuccess={handleServiceCreated}
            />

            <AddClinicServiceModal
                isOpen={showAddClinicServiceModal}
                onClose={() => {
                    setShowAddClinicServiceModal(false);
                    setSelectedService(null);
                }}
                onSuccess={handleClinicServiceAdded}
                selectedService={selectedService}
                clinicId={clinicId}
            />

            <EditClinicServiceModal
                isOpen={showEditClinicServiceModal}
                onClose={() => {
                    setShowEditClinicServiceModal(false);
                    setSelectedClinicService(null);
                }}
                onSuccess={handleClinicServiceUpdated}
                clinicServiceId={selectedClinicService?.id}
            />

            <ConfirmDialog
                isOpen={showDeleteConfirm}
                onClose={cancelDelete}
                onConfirm={confirmDelete}
                title="Remove Service"
                message={`Are you sure you want to remove "${serviceToDelete?.service?.name || 'this service'}" from the clinic? This action cannot be undone.`}
                confirmText="Remove"
                cancelText="Cancel"
                loading={deleteLoading}
                variant="danger"
            />
        </div>
    );
};

export default ClinicServicesTab; 